import { fork, put, select, takeLatest, takeLeading } from "redux-saga/effects";

import ApiClient from "../../../api/ApiClient";
import setupNotificationMessage from "../../../lib/notifications/setupNotificationMessage";
import { getAuthToken, getUserData } from "../auth/login/selector";
import { getLocale } from "../translations/selector";
import {
  fetchMoreNotifications,
  fetchNotifications,
  markAsRead,
  setGlobalLoading,
  SET_FILTER,
  SET_ALL_FILTERS,
  SET_FILTERS,
  RESET_FILTERS
} from "./index";
import { getFilters, getNotificationsTabs } from "./selector";
import qs from "qs";
import ApiClientAsync from "../../../api/ApiClientAsync";

function canDisplayNoNotifications({ notificationsTab, filters }) {
  if (notificationsTab.takeSumOfEnabledNotificationTypes) {
    for (const [_, filter] of Object.entries(filters)) {
      if (filter.type !== "checkbox") {
        continue;
      }

      if (filter.value) {
        return false;
      }
    }

    return true;
  }

  return notificationsTab.notificationTypes.every(
    ({ notificationType }) => filters[notificationType].value === false
  );
}

function getNotificationTypeParam({ currentNotificationsTab, filters }) {
  const {
    notificationTypes,
    takeSumOfEnabledNotificationTypes
  } = currentNotificationsTab;

  const notificationTypeParam = [];

  if (takeSumOfEnabledNotificationTypes) {
    for (const [name, filter] of Object.entries(filters)) {
      if (filter.type !== "checkbox" || filter.value === false) {
        continue;
      }

      notificationTypeParam.push(name);
    }
  } else {
    const enabledNotificationsTypes = notificationTypes
      .filter(({ notificationType }) => filters[notificationType].value)
      .map(({ notificationType }) => notificationType);

    notificationTypeParam.push(enabledNotificationsTypes);
  }

  return qs.stringify(
    {
      notification_type: notificationTypeParam,
      ...(filters.showOnlyUnread.value ? { read: false } : {}),
      ...(filters.searchQuery.value
        ? { search: filters.searchQuery.value }
        : {})
    },
    { arrayFormat: "repeat" }
  );
}

function* onFetchNotifications({ payload } = {}) {
  const shouldUseGlobalLoading = payload?.useGlobalLoading;
  const notificationsTabs = yield select(getNotificationsTabs);
  const filters = yield select(getFilters);
  const authToken = yield select(getAuthToken);
  const userData = yield select(getUserData);
  const locale = yield select(getLocale);

  if (!authToken) {
    yield put(fetchNotifications.failure());
    return;
  }

  const apiClientAsync = new ApiClientAsync(locale, authToken);

  try {
    const newNotificationsTabs = yield Promise.all(
      notificationsTabs.map(async notificationsTab => {
        if (canDisplayNoNotifications({ notificationsTab, filters })) {
          return {
            ...notificationsTab,
            unreadCount: 0,
            notifications: [],
            loading: false
          };
        }

        const query = getNotificationTypeParam({
          currentNotificationsTab: notificationsTab,
          filters
        });

        const data = await apiClientAsync.get({
          urlPath: "app.notification.browser",
          variables: { lastId: `?${query}` }
        });
        const notifications = data.messages.map(message => {
          return setupNotificationMessage(
            message,
            data.markets,
            data.tickers,
            userData
          );
        });
        return {
          ...notificationsTab,
          unreadCount: data.unread,
          notifications,
          loading: false
        };
      })
    );
    yield put(fetchNotifications.success(newNotificationsTabs));
  } catch (error) {
    yield put(fetchNotifications.failure());
    console.error(error);
  } finally {
    if (shouldUseGlobalLoading) {
      yield put(setGlobalLoading(false));
    }
  }
}

function* onFilterChangeFetchNotifications() {
  yield put(setGlobalLoading(true));
  yield put(fetchNotifications.trigger({ useGlobalLoading: true }));
}

function* onFetchMoreNotifications({ payload }) {
  const notificationsTabs = yield select(getNotificationsTabs);
  const filters = yield select(getFilters);
  const authToken = yield select(getAuthToken);
  const userData = yield select(getUserData);
  const notificationsTab = notificationsTabs.find(({ name }) => {
    return name === payload.name;
  });

  if (typeof notificationsTab == "undefined") {
    put(fetchMoreNotifications.failure());
    return;
  }

  if (!authToken) {
    yield put(fetchMoreNotifications.failure());
    return;
  }

  try {
    if (canDisplayNoNotifications({ notificationsTab, filters })) {
      return {
        ...notificationsTab,
        unreadCount: 0,
        notifications: [],
        loading: false
      };
    }

    const query = getNotificationTypeParam({
      currentNotificationsTab: notificationsTab,
      filters: filters
    });

    const notifications = notificationsTab.notifications;

    if (notifications.length <= 0) {
      yield put(fetchMoreNotifications.failure());
      return;
    }

    const lastId = notifications[notifications.length - 1].id;

    const { data } = yield ApiClient.get({
      urlPath: "app.notification.browser",
      variables: { lastId: `${lastId ?? ""}${query ? `?${query}` : ""}` }
    });

    const newNotifications = data.messages.map(message => {
      return setupNotificationMessage(
        message,
        data.markets,
        data.tickers,
        userData
      );
    });

    yield put(
      fetchMoreNotifications.success({
        ...notificationsTab,
        notifications: [...notifications, ...newNotifications],
        loading: false,
        endReached: newNotifications.length === 0
      })
    );
  } catch (error) {
    yield put(fetchMoreNotifications.failure());
    console.error(error);
  }
}

function* onMarkAsRead({ payload }) {
  const id = payload?.id;
  const notificationTypes =
    Array.isArray(payload?.notificationTypes) &&
    payload.notificationTypes.length > 0
      ? payload.notificationTypes
      : null;

  const requestData = {
    ...(id ? { id } : {}),
    ...(notificationTypes ? { notificationTypes } : {})
  };

  try {
    if (id) {
      yield ApiClient.put({
        urlPath: "app.notification.read",
        variables: requestData
      });
    } else {
      if (notificationTypes) {
        const query = qs.stringify(
          { notification_type: notificationTypes },
          { arrayFormat: "repeat", addQueryPrefix: true }
        );

        yield ApiClient.put({
          urlPath: "app.notification.read_all_by_type",
          variables: { query }
        });
      } else {
        yield ApiClient.put({
          urlPath: "app.notification.read_all"
        });
      }

      yield* onFetchNotifications();
    }

    yield put(markAsRead.success(requestData));
  } catch (error) {
    yield put(markAsRead.failure());
    console.error(error);
  }
}

function* watchFetchNewNotifications() {
  yield takeLatest(fetchNotifications.TRIGGER, onFetchNotifications);
  yield takeLeading(fetchMoreNotifications.TRIGGER, onFetchMoreNotifications);
  yield takeLatest(SET_FILTER, onFilterChangeFetchNotifications);
  yield takeLatest(SET_ALL_FILTERS, onFilterChangeFetchNotifications);
  yield takeLatest(SET_FILTERS, onFilterChangeFetchNotifications);
  yield takeLatest(RESET_FILTERS, onFilterChangeFetchNotifications);
  yield takeLatest(markAsRead, onMarkAsRead);
}

export default [fork(watchFetchNewNotifications)];
